/**
 * COL-010 land-expansion guidance.
 *
 * 45-node plot graph only: cheapest contiguous route from owned land to the
 * remaining East/North entitlement anchors. No navmesh or extra framework.
 */

import { entityDef } from '../config/balancing';
import { PARCEL_EAST, PARCEL_NORTH } from '../config/map';
import {
  EAST_MACRO_ANCHOR_PLOT_IDS,
  hasMacroEntitlement,
  islandPlots,
  landOwnsPlot,
  missingMacroAnchorPlots,
  plotById,
  plotCost,
  plotNeighbors,
  purchasableFrontierPlots,
} from '../spatial/plots';
import type { EntityKind, ParkState } from '../types';

export function purchasableFrontierPlotIds(unlockedLandIds: readonly string[]): string[] {
  return purchasableFrontierPlots(unlockedLandIds).map((plot) => plot.id);
}

export interface RouteResult {
  plotIds: string[];
  cost: number;
}

function cheaper(a: { cost: number; hops: number }, b: { cost: number; hops: number }): boolean {
  if (a.cost !== b.cost) {
    return a.cost < b.cost;
  }
  return a.hops < b.hops;
}

/** Cheapest contiguous unowned hop-list from owned land to `targetId`. */
export function cheapestRouteToPlot(
  unlockedLandIds: readonly string[],
  targetId: string,
): RouteResult | null {
  if (!plotById(targetId)) {
    return null;
  }
  if (landOwnsPlot(unlockedLandIds, targetId)) {
    return { plotIds: [], cost: 0 };
  }

  const dist = new Map<string, number>();
  const hops = new Map<string, number>();
  const prev = new Map<string, string | null>();
  const queue: Array<{ id: string; cost: number; hops: number }> = [];

  for (const plot of islandPlots()) {
    if (!landOwnsPlot(unlockedLandIds, plot.id)) {
      continue;
    }
    dist.set(plot.id, 0);
    hops.set(plot.id, 0);
    prev.set(plot.id, null);
    queue.push({ id: plot.id, cost: 0, hops: 0 });
  }
  if (queue.length === 0) {
    return null;
  }

  while (queue.length > 0) {
    queue.sort((a, b) => a.cost - b.cost || a.hops - b.hops || a.id.localeCompare(b.id));
    const cur = queue.shift()!;
    if (cur.cost !== dist.get(cur.id) || cur.hops !== hops.get(cur.id)) {
      continue;
    }
    if (cur.id === targetId) {
      break;
    }
    for (const neighbor of plotNeighbors(cur.id)) {
      const step = landOwnsPlot(unlockedLandIds, neighbor.id) ? 0 : plotCost(neighbor.id);
      const next = { id: neighbor.id, cost: cur.cost + step, hops: cur.hops + (step > 0 ? 1 : 0) };
      const knownCost = dist.get(neighbor.id);
      const knownHops = hops.get(neighbor.id) ?? Number.POSITIVE_INFINITY;
      if (knownCost !== undefined && !cheaper(next, { cost: knownCost, hops: knownHops })) {
        continue;
      }
      dist.set(neighbor.id, next.cost);
      hops.set(neighbor.id, next.hops);
      prev.set(neighbor.id, cur.id);
      queue.push(next);
    }
  }

  const total = dist.get(targetId);
  if (total === undefined) {
    return null;
  }
  const chain: string[] = [];
  let cursor: string | null = targetId;
  while (cursor && !landOwnsPlot(unlockedLandIds, cursor)) {
    chain.unshift(cursor);
    cursor = prev.get(cursor) ?? null;
  }
  return { plotIds: chain, cost: total };
}

export interface MacroGuidance {
  macro: string;
  missingAnchorIds: string[];
  routePlotIds: string[];
  nextFrontierPlotIds: string[];
  remainingCost: number;
}

export function guidanceForMacro(unlockedLandIds: readonly string[], macro: string): MacroGuidance {
  const missing = missingMacroAnchorPlots(unlockedLandIds, macro);
  const missingAnchorIds = missing.map((plot) => plot.id);
  if (missing.length === 0) {
    return { macro, missingAnchorIds, routePlotIds: [], nextFrontierPlotIds: [], remainingCost: 0 };
  }

  const routes = missing
    .map((plot) => cheapestRouteToPlot(unlockedLandIds, plot.id))
    .filter((route): route is RouteResult => route != null && route.plotIds.length > 0)
    .sort((a, b) => a.cost - b.cost || a.plotIds.length - b.plotIds.length || a.plotIds[0]!.localeCompare(b.plotIds[0]!));

  const nextFrontierPlotIds = [...new Set(routes.map((route) => route.plotIds[0]!).filter(Boolean))];
  const cheapest = routes[0];
  return {
    macro,
    missingAnchorIds,
    routePlotIds: cheapest?.plotIds ?? [],
    nextFrontierPlotIds,
    remainingCost: cheapest?.cost ?? 0,
  };
}

export function plotsOnCheapestMacroRoutes(unlockedLandIds: readonly string[], macro: string): Set<string> {
  const ids = new Set<string>();
  for (const plot of missingMacroAnchorPlots(unlockedLandIds, macro)) {
    const route = cheapestRouteToPlot(unlockedLandIds, plot.id);
    if (!route) {
      continue;
    }
    for (const id of route.plotIds) {
      ids.add(id);
    }
  }
  return ids;
}

export function catalogLandEntitlement(kind: EntityKind, unlockedLandIds: readonly string[]): string | null {
  const parcel = entityDef(kind).unlock.requiresParcel;
  if (!parcel || hasMacroEntitlement(unlockedLandIds, parcel)) {
    return null;
  }
  return parcel;
}

export function guidanceForKind(kind: EntityKind, unlockedLandIds: readonly string[]): MacroGuidance | null {
  const parcel = catalogLandEntitlement(kind, unlockedLandIds);
  if (!parcel) {
    return null;
  }
  return guidanceForMacro(unlockedLandIds, parcel);
}

/** Direct-purchase targets: the locked plots that actually satisfy the attraction entitlement. */
export function recommendedPlotIdsForKind(kind: EntityKind, unlockedLandIds: readonly string[]): string[] {
  return guidanceForKind(kind, unlockedLandIds)?.missingAnchorIds ?? [];
}

export type LandMilestoneKind = 'east' | 'north';

export interface PlotPurchaseGuidance {
  plotId: string;
  cost: number;
  affordable: boolean;
  recommended: boolean;
  milestone: { kind: LandMilestoneKind; have: number; need: number } | null;
  advancesKind: 'ferris_wheel' | 'roller_coaster' | null;
}

function milestoneForMacro(
  unlockedLandIds: readonly string[],
  macro: string,
): { kind: LandMilestoneKind; have: number; need: number } | null {
  if (macro === PARCEL_EAST) {
    const need = EAST_MACRO_ANCHOR_PLOT_IDS.length;
    const have = EAST_MACRO_ANCHOR_PLOT_IDS.filter((id) => landOwnsPlot(unlockedLandIds, id)).length;
    return { kind: 'east', have, need };
  }
  if (macro === PARCEL_NORTH) {
    return { kind: 'north', have: hasMacroEntitlement(unlockedLandIds, PARCEL_NORTH) ? 1 : 0, need: 1 };
  }
  return null;
}

export function plotPurchaseGuidance(
  state: ParkState,
  plotId: string,
  recommendedPlotIds: readonly string[] = [],
): PlotPurchaseGuidance {
  const eastRoute = plotsOnCheapestMacroRoutes(state.unlockedLandIds, PARCEL_EAST);
  const northRoute = plotsOnCheapestMacroRoutes(state.unlockedLandIds, PARCEL_NORTH);
  const onEast = eastRoute.has(plotId);
  const onNorth = northRoute.has(plotId);
  let milestone: PlotPurchaseGuidance['milestone'] = null;
  let advancesKind: PlotPurchaseGuidance['advancesKind'] = null;
  if (onEast) {
    milestone = milestoneForMacro(state.unlockedLandIds, PARCEL_EAST);
    advancesKind = 'ferris_wheel';
  } else if (onNorth) {
    milestone = milestoneForMacro(state.unlockedLandIds, PARCEL_NORTH);
    advancesKind = 'roller_coaster';
  }
  const cost = plotCost(plotId);
  return {
    plotId,
    cost,
    affordable: state.coins >= cost,
    recommended: recommendedPlotIds.includes(plotId),
    milestone,
    advancesKind,
  };
}
